/**
 * SceneReviewGate — Universal post-scene confidence rating gate.
 *
 * Intercepts any scene's onComplete callback and shows the Anki-style
 * Again / Hard / Good / Easy overlay before proceeding.
 *
 * Works for ALL scene types across ALL domains. FlashcardPlayer has its own
 * built-in rating (pass enabled=false to skip the gate for flashcards).
 *
 * Usage — component wrapper:
 *   <SceneReviewGate
 *     skillId={segment.skill_id || domain}
 *     learnerId={learnerId}
 *     onComplete={proceedToNext}
 *   >
 *     <SomeLearningScene onComplete={gateOnComplete} />
 *   </SceneReviewGate>
 *
 * Usage — hook form (for inline injection into complex renderers):
 *   const { gatedComplete, ratingOverlay } = useSceneReviewGate({
 *     skillId, learnerId, onComplete, enabled
 *   });
 *   // pass gatedComplete as onComplete to the scene
 *   // render ratingOverlay anywhere in the tree
 *
 * ADAPTIVE API:
 *   POST /scenario-os/adaptive/review/{learnerId}/{skillId}/rate
 *   body: { confidence: 1|2|3|4, time_taken_seconds: number }
 *   → updates learner_review_cards (stability, retrievability, next_eval_date)
 *
 * SKILL ID RESOLUTION ORDER:
 *   1. segment/scene.skill_id     (explicit — most specific)
 *   2. segment/scene.category_id  (LSAT category like lr_flaw)
 *   3. domain + "." + scene_type  (e.g. "ai.code_lab")
 *   4. domain                     (coarsest fallback)
 *
 * DOMAIN-AGNOSTIC:
 *   No domain-specific logic here. Works for LSAT, AI, Football, Biology, etc.
 *   Skill IDs go into the adaptive engine which handles domain-specific mastery.
 *
 * EXCLUDED SCENE TYPES:
 *   - flashcard (already has built-in rating — pass enabled=false)
 *   - timed_test_session (multi-section, uses its own scoring)
 *   - score_report / test_review (not learning activities)
 */

import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  createContext,
  useContext,
} from 'react';

const API_BASE = '/scenario-os';

// Scene types that have their own rating and should NOT get the gate
const SELF_RATED_TYPES = new Set([
  'flashcard',
  'timed_test_session',
  'passage_question_set',
  'score_report',
  'test_review',
]);

// ── Auth helpers (mirrors FlashcardPlayer pattern) ────────────────────────────

function getStoredToken() {
  return (
    localStorage.getItem('scenario_os_token') ||
    sessionStorage.getItem('scenario_os_token') ||
    ''
  );
}

function getLearnerId() {
  try {
    const stored = localStorage.getItem('scenario_os_learner');
    if (stored) {
      const data = JSON.parse(stored);
      return data.learner_id || data.sub || '';
    }
    const tok = getStoredToken();
    if (tok && tok.split('.').length >= 2) {
      const payload = JSON.parse(
        atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      );
      return payload.learner_id || payload.sub || '';
    }
  } catch { /* ignore */ }
  return '';
}

function authHeaders() {
  const tok = getStoredToken();
  return {
    'Content-Type': 'application/json',
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
}

// ── Skill ID resolver ─────────────────────────────────────────────────────────

export function resolveSkillId(sceneOrSegment, domain) {
  if (!sceneOrSegment) return domain || '';
  const s = sceneOrSegment;
  return (
    s.skill_id ||
    s.category_id ||
    (s.domain_metadata?.skill_id) ||
    (domain && s.scene_type ? `${domain}.${s.scene_type}` : null) ||
    domain ||
    ''
  );
}

// ── Core async rate function (reusable from Anki callbacks too) ───────────────

export async function postAdaptiveReview(learnerId, skillId, rating, timeTakenSeconds = 0) {
  if (!learnerId || !skillId || learnerId === 'anon') return;
  try {
    await fetch(
      `${API_BASE}/adaptive/review/${encodeURIComponent(learnerId)}/${encodeURIComponent(skillId)}/rate`,
      {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ confidence: rating, time_taken_seconds: timeTakenSeconds }),
      }
    );
  } catch {
    // fire-and-forget — adaptive rating must never block UX
  }
}

// ── Rating button config ──────────────────────────────────────────────────────

const RATINGS = [
  { value: 1, label: 'Again', color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  key: '1' },
  { value: 2, label: 'Hard',  color: '#f97316', bg: 'rgba(249,115,22,0.1)', key: '2' },
  { value: 3, label: 'Good',  color: '#22c55e', bg: 'rgba(34,197,94,0.1)',  key: '3' },
  { value: 4, label: 'Easy',  color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', key: '4' },
];

// ── Rating panel UI ───────────────────────────────────────────────────────────

function RatingPanel({ onRated, onDismiss, autoDismissMs = 9000 }) {
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(Math.round(autoDismissMs / 1000));
  const [visible, setVisible] = useState(false);
  const startRef = useRef(Date.now());
  const timerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    timerRef.current = setTimeout(() => onDismiss?.(), autoDismissMs);
    const cd = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(cd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRate = useCallback(async (value) => {
    if (submitting) return;
    setSubmitting(true);
    clearTimeout(timerRef.current);
    const timeTaken = Math.round((Date.now() - startRef.current) / 1000);
    await onRated?.(value, timeTaken);
  }, [submitting, onRated]);

  // Keyboard shortcuts 1-4
  useEffect(() => {
    const handler = (e) => {
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= 4) handleRate(n);
      if (e.key === 'Escape') onDismiss?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleRate, onDismiss]);

  return (
    <div
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 300,
        padding: '0 16px 16px',
        display: 'flex', justifyContent: 'center',
        transform: visible ? 'translateY(0)' : 'translateY(100%)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.22s cubic-bezier(0.34,1.56,0.64,1), opacity 0.2s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Rate your confidence"
    >
      <div style={{
        background: '#1e293b', border: '1px solid #334155', borderRadius: 14,
        padding: '18px 22px', maxWidth: 560, width: '100%',
        boxShadow: '0 -4px 32px rgba(0,0,0,0.45)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f8fafc' }}>
            How confident were you?
          </span>
          <button
            onClick={onDismiss}
            style={{ background: 'none', border: 'none', color: '#64748b',
              cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}
            aria-label="Skip rating"
          >✕</button>
        </div>
        <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 14px' }}>
          Your rating schedules the next review.
          {countdown > 0 && (
            <span style={{ marginLeft: 6, color: '#475569' }}>
              Auto-skip in {countdown}s
            </span>
          )}
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          {RATINGS.map(({ value, label, color, bg, key }) => (
            <button
              key={value}
              onClick={() => handleRate(value)}
              disabled={submitting}
              aria-label={`${label} (press ${key})`}
              title={`${label} — press ${key}`}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: '12px 8px', borderRadius: 10,
                border: `2px solid ${color}`, cursor: 'pointer',
                background: bg, color,
                opacity: submitting ? 0.6 : 1,
                transition: 'transform 0.1s',
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700 }}>{label}</span>
              <span style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>{key}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Hook: useSceneReviewGate ──────────────────────────────────────────────────

/**
 * useSceneReviewGate — inject a review gate into any segment/scene renderer.
 *
 * @param {object} options
 * @param {string|object} options.sceneOrSegment  — scene/segment doc (for skill resolution)
 * @param {string}        options.domain          — domain_id fallback
 * @param {string}        [options.learnerId]     — overrides auto-detected learner
 * @param {function}      options.onComplete      — real completion callback
 * @param {boolean}       [options.enabled=true]  — false for self-rated scene types
 *
 * @returns {{ gatedComplete: function, ratingOverlay: ReactNode }}
 */
export function useSceneReviewGate({
  sceneOrSegment,
  domain,
  learnerId: learnerIdProp,
  onComplete,
  enabled = true,
}) {
  const [showRating, setShowRating] = useState(false);
  const pendingRef = useRef(null);

  const learnerId = learnerIdProp || getLearnerId();
  const skillId = resolveSkillId(sceneOrSegment, domain);

  // Determine if this scene type has its own rating
  const sceneType = sceneOrSegment?.scene_type || sceneOrSegment?.segment_type;
  const shouldGate = enabled && !SELF_RATED_TYPES.has(sceneType);

  const gatedComplete = useCallback((...args) => {
    if (!shouldGate) {
      onComplete?.(...args);
      return;
    }
    pendingRef.current = args;
    setShowRating(true);
  }, [shouldGate, onComplete]);

  const handleRated = useCallback(async (rating, timeTaken) => {
    setShowRating(false);
    await postAdaptiveReview(learnerId, skillId, rating, timeTaken);
    onComplete?.(...(pendingRef.current || []));
  }, [learnerId, skillId, onComplete]);

  const handleDismiss = useCallback(() => {
    setShowRating(false);
    onComplete?.(...(pendingRef.current || []));
  }, [onComplete]);

  const ratingOverlay = showRating ? (
    <RatingPanel onRated={handleRated} onDismiss={handleDismiss} />
  ) : null;

  return { gatedComplete, ratingOverlay };
}

// ── Component: SceneReviewGate ────────────────────────────────────────────────

/**
 * SceneReviewGate — wraps a scene component and intercepts its onComplete.
 *
 * Clones the single child and replaces its `onComplete` prop with the gated version.
 */
export default function SceneReviewGate({
  sceneOrSegment,
  domain,
  learnerId,
  onComplete,
  enabled = true,
  children,
}) {
  const { gatedComplete, ratingOverlay } = useSceneReviewGate({
    sceneOrSegment,
    domain,
    learnerId,
    onComplete,
    enabled,
  });

  // Inject gatedComplete into the single child's onComplete prop
  const gatedChild = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child, { onComplete: gatedComplete });
  });

  return (
    <>
      {gatedChild}
      {ratingOverlay}
    </>
  );
}
